// @flow
import React, {Component, type Node} from 'react';
import {compose} from 'redux';
import {connect} from 'react-redux';
import Experiment from '@homeaway/react-experiment';
import GoogleOneTapLogin from '@homeaway/ha-google-one-tap';
import LoginExperience from '@homeaway/react-lex';
import SiteHeader from '@homeaway/react-site-header';
import TransitionBanner from '@homeaway/react-brand-transition-banner';
import breakpoints from '../../utils/breakpoints';
import withLingua from '../withLingua/withLingua';
import type {LinguaProps} from '../withLingua/withLingua';

import {
    HA_IDA_SERP_GOOGLEONETAP,
    HA_IDA_SERP_HEADER_LOGIN_TEST,
    HA_IDA_V3_LOGIN_TEST,
    HA_GX_PRE_BRAND_TRANSITION,
    HA_GX_POST_BRAND_TRANSITION
} from '../../abacus/experiments';

import type {State as ReduxState} from '../../reducers/index';

const LINGUA_KEYS = {
    preTitle: 'pre.transition.title',
    preInfo: 'pre.transition.info',
    postTitle: 'post.transition.title',
    postInfo: 'post.transition.info'
};

type StateProps = {
    siteName: string,
    theme: string,
    isMobile: boolean,
    fullLocale: string,
    currency: string,
    brand: string,
    headerData: {}
};

type OwnProps = {
    internalTools?: Node,
    showBrandTransitionBanner: string
};

type Props = OwnProps & StateProps & LinguaProps;

type State = {
    googleCredentialsReported: boolean,
    displayNotificationJewel: boolean,
    displayGoogleFrame: boolean,
    loginModalOpen: boolean,
    isUserLogged: boolean
};

export class HeaderBar extends Component<Props, State> {
    constructor(props: Props) {
        super(props);

        this.state = {
            googleCredentialsReported: false,
            displayNotificationJewel: false,
            displayGoogleFrame: false,
            loginModalOpen: false,
            isUserLogged:
                props.headerData.hasOwnProperty('traveler') ||
                props.headerData.hasOwnProperty('owner')
        };
    }

    handleShowModal = () => {
        this.setState({loginModalOpen: true});
    };

    handleCloseModal = () => {
        this.setState({loginModalOpen: false});
    };

    handleLoginSuccess = (options: Object) => {
        if (options && options.publicUuid) {
            window.analyticsdatalayer.publicuuid = options.publicUuid;
        }
        this.setState({isUserLogged: true});
    };

    handleTrayChange = (opened: boolean) => {
        if (opened) {
            this.setState({
                displayGoogleFrame: true
            });
        } else {
            this.setState({
                displayGoogleFrame: false,
                displayNotificationJewel: false
            });
        }
    };

    handleGoogleCredentialsAvailable = () => {
        if (this.state.googleCredentialsReported) {
            return;
        }
        this.setState({
            displayNotificationJewel: true,
            googleCredentialsReported: true
        });
    };

    render() {
        let {
            siteName,
            theme,
            isMobile,
            internalTools,
            fullLocale,
            currency,
            brand,
            showBrandTransitionBanner,
            headerData,
            messages
        } = this.props;

        const {
            displayGoogleFrame,
            displayNotificationJewel,
            isUserLogged,
            loginModalOpen
        } = this.state;

        const loginProps = {
            modalMode: true,
            locale: fullLocale,
            site: siteName,
            siteName: brand,
            handleCloseModal: this.handleCloseModal,
            handleLoginSuccess: this.handleLoginSuccess
        };

        const siteHeaderProps = {
            dataUrl: '/serp/api/header',
            siteName,
            theme,
            isMobile,
            data: headerData,
            displayTrayJewel: displayNotificationJewel,
            fetchUserData: isUserLogged,
            fullLocale,
            onTrayChange: this.handleTrayChange,
            internalTools,
            currencyCode: currency,
            mobileBreakpoint: breakpoints.MD_MIN - 1,
            withoutCountryLanguageSelector: false,
            withoutCurrencySelector: false
        };

        const preTitle = messages[LINGUA_KEYS.preTitle];
        const preInfo = messages[LINGUA_KEYS.preInfo];
        const postTitle = messages[LINGUA_KEYS.postTitle];
        const postInfo = messages[LINGUA_KEYS.postInfo];

        return (
            <div className="HeaderBar" role="group">
                <Experiment name={HA_IDA_SERP_HEADER_LOGIN_TEST}>
                    {({On, Off}) => (
                        <React.Fragment>
                            <On>
                                <SiteHeader
                                    {...siteHeaderProps}
                                    travelerLoginClickHandler={
                                        this.handleShowModal
                                    }
                                />
                            </On>
                            <Off>
                                <SiteHeader {...siteHeaderProps} />
                            </Off>
                        </React.Fragment>
                    )}
                </Experiment>

                <Experiment name={HA_GX_PRE_BRAND_TRANSITION} variant={1}>
                    <TransitionBanner
                        forPreTransition
                        title={preTitle}
                        info={preInfo}
                    />
                </Experiment>
                {showBrandTransitionBanner && (
                    <Experiment name={HA_GX_POST_BRAND_TRANSITION} variant={1}>
                        <TransitionBanner
                            forPreTransition={false}
                            title={postTitle}
                            info={postInfo}
                        />
                    </Experiment>
                )}
                {loginModalOpen && (
                    <Experiment name={HA_IDA_V3_LOGIN_TEST}>
                        {({On, Off}) => (
                            <React.Fragment>
                                <On>
                                    <LoginExperience
                                        useNewEndpoints
                                        {...loginProps}
                                    />
                                </On>
                                <Off>
                                    <LoginExperience {...loginProps} />
                                </Off>
                            </React.Fragment>
                        )}
                    </Experiment>
                )}
                {!isUserLogged && (
                    <Experiment name={HA_IDA_SERP_GOOGLEONETAP} variant={1}>
                        <GoogleOneTapLogin
                            displayIFrame={displayGoogleFrame}
                            onCredentialsAvailable={
                                this.handleGoogleCredentialsAvailable
                            }
                            focus
                            closeOnFocusOut
                            loginSuccessCallback={this.handleLoginSuccess}
                        />
                    </Experiment>
                )}
            </div>
        );
    }
}

const mapStateToProps = ({
    system: {siteName, theme, isMobile, fullLocale, currency, brand},
    header
}: ReduxState): StateProps => ({
    siteName,
    theme,
    isMobile,
    fullLocale,
    currency,
    brand,
    headerData: header
});

export default compose(
    connect(mapStateToProps),
    withLingua({
        namespace: 'gx/globalization',
        category: 'rebrandingBrandTransition'
    })
)(HeaderBar);
